import React, { useState, useEffect, useMemo } from 'react';
import { Globe, CheckCircle, AlertCircle } from 'lucide-react';
import { useSeller } from '../../context/SellerContext';
import { sellerAccountService } from '../../services/sellerAccountService';

/**
 * Shared MarketplaceSelector component.
 *
 * Loads authorized channels for the selected seller from context.
 * Renders a compact platform dropdown (or badges).
 *
 * Props:
 *   value        - currently selected sellerAccountId (or '')
 *   onChange     - (sellerAccountId) => void
 *   showAll      - show "All Marketplaces" option (default true)
 *   className    - additional classes
 *   size         - 'sm' | 'md' (default 'sm')
 */

// Platform SVG Icons
function AmazonIcon({ size = 14 }) {
  return <img src="/assets/marketplaces/amazon-icon.png" alt="Amazon" width={size} height={size} className="object-contain" />;
}

function FlipkartIcon({ size = 14 }) {
  return <img src="/assets/marketplaces/flipkart-icon.png" alt="Flipkart" width={size} height={size} className="object-contain" />;
}

function MeeshoIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#6D2077"/>
      <path d="M7 8.5C7 7.672 7.672 7 8.5 7s1.5.672 1.5 1.5V16H7V8.5zM11 10.5c0-.828.672-1.5 1.5-1.5s1.5.672 1.5 1.5V16h-3v-5.5zM15 8.5c0-.828.672-1.5 1.5-1.5s1.5.672 1.5 1.5V16h-3V8.5z" fill="white"/>
    </svg>
  );
}

const PlatformIcon = ({ platform, size = 14 }) => {
  switch (platform) {
    case 'AMAZON': return <AmazonIcon size={size} />;
    case 'FLIPKART': return <FlipkartIcon size={size} />;
    case 'MEESHO': return <MeeshoIcon size={size} />;
    default: return <Globe size={size} className="text-slate-400" />;
  }
};

const PLATFORM_LABELS = {
  AMAZON: 'Amazon',
  FLIPKART: 'Flipkart',
  MEESHO: 'Meesho',
};

export default function MarketplaceSelector({ value = '', onChange, showAll = true, className = '', size = 'sm', sellerId: propSellerId }) {
  const { selectedSeller } = useSeller();
  const [channels, setChannels] = useState([]);

  // Use prop sellerId if provided (hub mode), otherwise use context
  const effectiveSellerId = propSellerId || selectedSeller?.id;

  // Load channels from seller context, prop, or fetch
  useEffect(() => {
    if (!propSellerId && selectedSeller?.channels && selectedSeller.channels.length > 0) {
      setChannels(selectedSeller.channels.filter(c => c.accountStatus === 'AUTHORIZED'));
    } else if (effectiveSellerId) {
      // Fetch from API for the given sellerId
      sellerAccountService.getAll().then(res => {
        const all = Array.isArray(res.data || res) ? (res.data || res) : [];
        setChannels(all.filter(a =>
          a.sellerId === effectiveSellerId && a.accountStatus === 'AUTHORIZED'
        ));
      }).catch(() => { setChannels([]); });
    } else {
      setChannels([]);
    }
  }, [selectedSeller, effectiveSellerId, propSellerId]);

  // Auto-select first channel if only one and no value set
  useEffect(() => {
    if (!showAll && channels.length === 1 && !value) {
      onChange(channels[0].id);
    }
  }, [channels, showAll, value, onChange]);

  if (channels.length === 0) return null;

  // If only one channel, show it as a badge (no dropdown needed)
  if (channels.length === 1 && !showAll) {
    const ch = channels[0];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-50 border border-slate-200 text-slate-600 ${className}`}>
        <PlatformIcon platform={ch.platform} size={13} />
        {PLATFORM_LABELS[ch.platform] || ch.platform}
      </span>
    );
  }

  const isCompact = size === 'sm';

  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`${isCompact ? 'px-3 py-[7px] text-[13px]' : 'px-3 py-2 text-sm'} border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] transition-all ${className}`}
    >
      {showAll && <option value="">All Marketplaces</option>}
      {channels.map(ch => (
        <option key={ch.id} value={ch.id}>
          {PLATFORM_LABELS[ch.platform] || ch.platform}
          {ch.sellerName ? ` — ${ch.sellerName}` : ''}
        </option>
      ))}
    </select>
  );
}

/**
 * Hook: useSellerChannels
 * Returns the authorized channels for the selected seller.
 */
export function useSellerChannels() {
  const { selectedSeller } = useSeller();

  return useMemo(() => {
    if (!selectedSeller) return [];
    return (selectedSeller.channels || []).filter(c => c.accountStatus === 'AUTHORIZED');
  }, [selectedSeller]);
}
